import React, { Dispatch, MouseEvent, SetStateAction, useCallback, useState } from 'react';
import { Button, Form, Popover, Radio, RadioChangeEvent } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import { REPORT_TYPE_OPTIONS } from '../../../constants';
import { COLORS } from '../../../../../../utils/cssUtils';
import { useContextReports } from '../../../../../../context/reports';
import { IReport, useReportUpdateReportType } from '../../../../../../hooks/reports';
import { EnumReportTypes } from '../../../../../../types/reports';
import './style.less';
import { calculatePropertyTaxesPercentage } from '../../../../../../utils/calculatePropertyTaxesPercentage';
import { calculatePropertyTaxes } from '../../../../../../utils/calculatePropertyTaxes';
import { calculateTotal } from '../../../../../../utils/calculateTotal';
import { calculateLoanAmountWithUip } from '../../../../../../utils/calculateLoanAmountWithUip';
import { calculateClosingCosts } from '../../../../../../utils/calculateClosingCosts';
import loansClosingCosts from '../../../ClosingCostsDetails/LoansClosingCosts';

interface ILoneTypeToggleProps {
  report: IReport | undefined;
  reportType: EnumReportTypes | undefined;
  setReportType: Dispatch<SetStateAction<EnumReportTypes | undefined>>;
}

const LoneTypeToggle = ({ report, setReportType, reportType }: ILoneTypeToggleProps) => {
  const form = Form.useFormInstance();
  const loans = useWatch('loans', form) as any[];

  const [popoverVisible, setPopoverVisible] = useState(false);
  const changeReportType = useReportUpdateReportType();
  const {
    getSettings,
    getReport,
    handleLoansUpdate,
    reportLoanClosingCosts,
    reportClosingCosts
  } = useContextReports();

  const onTypeChange = ({ target }: RadioChangeEvent) => {
    const value = target.value as EnumReportTypes;

    setReportType(value);
    if (report) {
      const isRefinance = value === EnumReportTypes.refinance;

      changeReportType.fetch({ reportType: value }, report.id).then(async () => {
        getReport?.();
        await getSettings?.(value);

        if (loans?.length) {
          const updatedLoans = loans?.map((loan, index) => {
            const isFirstItemInRefinance = isRefinance && index === 0;
            const propertyTaxes = calculatePropertyTaxes(form, index);

            const total = calculateTotal(form, index, { propertyTaxes })

            const totalClosingCosts = calculateClosingCosts(form, index, reportLoanClosingCosts, reportClosingCosts)

            const loanAmountWithUip = calculateLoanAmountWithUip(
              form,
              index,
              {
                closingCosts: isRefinance && !isFirstItemInRefinance ? totalClosingCosts : 0,
                reportType: value
              }
            )

            return {
              ...loan,
              total: { value: total },
              totalClosingCosts: { totalClosingCosts: total },
              loanAmountWithUip: { value: loanAmountWithUip },
              propertyTaxes: {
                value: propertyTaxes
              },
              ...(isRefinance && { hidden: index !== 0 })
            }
          })

          form.setFieldValue('loans', updatedLoans);
          handleLoansUpdate?.();
        }


      }).finally(() => {
        setPopoverVisible(false);
      });
    }
  };

  const handleMouseEnter = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.borderColor = COLORS.White;
    e.currentTarget.style.color = COLORS.White;
  }, []);

  const handleMouseLeave = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.borderColor = COLORS.BorderGrey;
    e.currentTarget.style.color = COLORS.BorderGrey;
  }, []);

  const handlePopoverVisibleChange = (visible: boolean) => {
    setPopoverVisible(visible);
  };

  return (
    <Popover
      trigger='click'
      placement='bottom'
      showArrow={false}
      open={popoverVisible}
      onOpenChange={handlePopoverVisibleChange}
      content={
        <Radio.Group
          options={REPORT_TYPE_OPTIONS}
          onChange={onTypeChange}
          value={reportType}
          optionType='button'
          buttonStyle='solid'
          className='custom-radio-group-report' />
      }
    >
      <Button
        ghost
        size='large'
        type='default'
        style={{
          borderColor: COLORS.BorderGrey,
          color: COLORS.BorderGrey
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}>
        Report type
      </Button>
    </Popover>
  );
};

export default LoneTypeToggle;
